import Component from "@base/Component.js";
import CockpitPanel from "./CockpitPanel.js";
import config from "@/config.js";
import t from "@/services/translationService.js";
import galleryController from "@modules/gallery/galleryController.js";

/**
 * Cockpit → Landing Page Top Image Editor. Owns the welcome image behind the
 * landing page's diagonal title panel (the Hero component now only displays it).
 *
 * Two places this deliberately differs from the list sections (#132):
 *
 * Removing confirms in place instead of from inside an editor. The list
 * sections put delete behind opening a row precisely so it takes two steps;
 * here there is one value and no row to open, so the confirm dialog *is* the
 * second step. Inventing a modal to hold one button would be ceremony, not
 * consistency.
 *
 * The no-image state is the framed placeholder rather than CockpitPanel.empty(),
 * because the panel still has to show what it is you would be replacing, and an
 * empty-state message would replace the preview it is describing.
 *
 * The overlay's "Picturefox" / "by Dario Bolzern" are brand text, reproduced
 * here so the preview matches the real landing page; they are not translated
 * anywhere on the site.
 *
 * props: { lang }
 */
export default class HeroManagerPanel extends CockpitPanel {
  constructor(props) {
    super(props);
    this.state = { busy: false, error: "", heroImage: null };
  }

  async onBeforeMount() {
    try {
      await galleryController.ensureLoaded();
      this.state.heroImage = galleryController.manager.heroImage ?? null;
    } catch (err) {
      this.state.error = err.message || t.t("common.error");
    }
  }

  title() {
    return Component.escape(t.t("cockpit.nav.hero"));
  }

  lead() {
    return Component.escape(t.t("cockpit.hero.lead"));
  }

  /**
   * The upload sits top-right like every other section's primary action. It is
   * a <label> wrapping a hidden file input rather than a button, because the
   * action *is* picking a file — there is no dialog in between.
   */
  actions() {
    const { busy, heroImage } = this.state;
    const label = busy ? t.t("common.loading") : heroImage ? t.t("cockpit.hero.replace") : t.t("cockpit.hero.upload");
    return `<label class="btn-primary cockpit-hero__upload">
              ${Component.escape(label)}
              <input type="file" accept="image/*" class="cockpit-hero__file" hidden ${busy ? "disabled" : ""} />
            </label>`;
  }

  body() {
    const { heroImage, busy, error } = this.state;

    const preview = heroImage
      ? `<img class="cockpit-hero__img" src="${config.imageBase}/site/${encodeURIComponent(heroImage)}" alt="" />`
      : `<div class="cockpit-hero__img cockpit-hero__img--empty"><span>${Component.escape(t.t("cockpit.hero.none"))}</span></div>`;

    return `
      ${error ? CockpitPanel.error(error) : ""}
      <div class="cockpit-hero">
        <div class="cockpit-hero__frame">
          ${preview}
          <div class="cockpit-hero__overlay">
            <span class="cockpit-hero__overlay-kicker">by Dario Bolzern</span>
            <span class="cockpit-hero__overlay-title">Picturefox</span>
          </div>
        </div>
        <div class="cockpit-hero__side">
          <p class="cockpit-hero__hint">${Component.escape(t.t("cockpit.hero.hint"))}</p>
          <div class="cockpit-hero__controls">
            ${
              heroImage
                ? `<button type="button" class="btn-danger cockpit-hero__remove" ${busy ? "disabled" : ""}>${Component.escape(t.t("cockpit.hero.remove"))}</button>`
                : ""
            }
          </div>
        </div>
      </div>`;
  }

  onMounted() {
    const file = this.$(".cockpit-hero__file");
    if (file) {
      this.listen(file, "change", async () => {
        const picked = file.files?.[0];
        if (!picked) return;
        this.setState({ busy: true, error: "" });
        try {
          const name = await galleryController.uploadHero(picked);
          this.setState({ busy: false, heroImage: name });
        } catch (err) {
          this.setState({ busy: false, error: err.message || t.t("common.error") });
        }
      });
    }

    const remove = this.$(".cockpit-hero__remove");
    if (remove) {
      this.listen(remove, "click", async () => {
        if (!window.confirm(t.t("cockpit.hero.confirmRemove"))) return;
        this.setState({ busy: true, error: "" });
        try {
          await galleryController.deleteHero();
          this.setState({ busy: false, heroImage: null });
        } catch (err) {
          this.setState({ busy: false, error: err.message || t.t("common.error") });
        }
      });
    }
  }
}
